import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Lightbulb, RotateCcw, ArrowRight, BookOpen } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, formatApiError } from "@/lib/api";
import { useCourse } from "@/lib/hooks";
import { PageShell } from "@/components/AppShell";
import { Loader } from "@/components/ProtectedRoute";
import { Textarea } from "@/components/ui/textarea";
import { TaskGroup, AnswerView } from "@/components/lab/TaskGroup";
import { LabResult } from "@/components/lab/LabResult";

const STEPS = ["Inspect", "Decide", "Explain", "Debrief"];

const Evidence = ({ cards }) => {
  const [open, setOpen] = useState(cards[0]?.id);
  const [all, setAll] = useState(false);
  const c = cards.find((x) => x.id === open);
  const shown = all ? cards : cards.slice(0, 6);
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {shown.map((x) => <button key={x.id} type="button" data-testid={`evidence-card-${x.id}`} onClick={() => setOpen(x.id)} className={`border px-2.5 py-1.5 text-left text-xs ${open === x.id ? "border-brand-data bg-brand-data/15" : "border-border bg-card hover:bg-muted"}`}><span className="font-mono">{x.id}</span> · {x.type?.replace(/_/g, " ")}</button>)}
        {cards.length > 6 && <button type="button" data-testid="evidence-more" onClick={() => setAll(!all)} className="px-2 text-xs font-semibold text-brand-primary">{all ? "Show fewer" : `+${cards.length - 6} more`}</button>}
      </div>
      {c && (
        <div data-testid="evidence-detail" className="mt-3 border border-brand-data/50 bg-card p-4 text-sm">
          <div className="font-semibold">{c.title}</div>
          <p className="mt-2 leading-relaxed">{c.content}</p>
          <dl className="mt-3 grid gap-1 text-xs text-muted-foreground">
            <div><dt className="inline font-semibold">Source: </dt><dd className="inline">{c.source}</dd></div>
            <div><dt className="inline font-semibold">Time / version: </dt><dd className="inline">{c.timestamp_or_version}</dd></div>
            <div><dt className="inline font-semibold">Quality / permission: </dt><dd className="inline">{c.quality_or_permission_note}</dd></div>
          </dl>
        </div>
      )}
    </div>
  );
};

const Stepper = ({ step }) => (
  <div className="flex gap-1">{STEPS.map((s, i) => <div key={s} className={`flex-1 border-t-2 pt-1 font-mono text-[10px] uppercase tracking-wider ${i <= step ? "border-brand-primary text-foreground" : "border-border text-muted-foreground"}`}>{s}</div>)}</div>
);

export default function LabPlayer() {
  const { scenarioId } = useParams();
  const qc = useQueryClient();
  const { data: course } = useCourse();
  const { data, refetch } = useQuery({ queryKey: ["lab", scenarioId], queryFn: async () => (await api.get(`/labs/${scenarioId}`)).data });
  const [tasks, setTasks] = useState({});
  const [choice, setChoice] = useState(null);
  const [explanation, setExplanation] = useState("");
  const [hints, setHints] = useState(0);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const started = useRef(Date.now());

  useEffect(() => {
    setResult(null);
    setTasks({});
    setChoice(null);
    setExplanation("");
    setHints(0);
    started.current = Date.now();
  }, [scenarioId]);

  if (!data || !course) return <Loader />;
  const { lab, attempts } = data;
  const mod = course.modules.find((m) => m.id === lab.module_id);
  const labs = mod.lessons.filter((l) => l.kind === "lab");
  const required = labs.some((l) => l.scenario_id === scenarioId);
  const retry = attempts.length > 0;
  const setTask = (id, patch) => setTasks({ ...tasks, [id]: { selected: [], text: "", ...tasks[id], ...patch } });
  const step = result ? 3 : explanation.length >= 80 ? 2 : choice || Object.keys(tasks).length ? 1 : 0;

  const submit = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/labs/${scenarioId}/submit`, { choice_id: choice, tasks, explanation, hints_used: hints, time_spent_sec: Math.round((Date.now() - started.current) / 1000) });
      setResult(r);
      qc.invalidateQueries({ queryKey: ["progress"] });
      refetch();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const tryAgain = () => { setResult(null); setTasks({}); setChoice(null); setExplanation(""); setHints(0); started.current = Date.now(); window.scrollTo({ top: 0 }); };

  return (
    <PageShell wide>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Link to={`/modules/${lab.module_id}`} className="overline-label hover:text-foreground">{lab.module_id} · {mod.studio || mod.title}</Link>
        <span data-testid="lab-counter" className="font-mono text-xs">{required ? "Required plant decision" : "Optional · Try another case"} · ~{lab.estimated_minutes} min</span>
      </div>
      <h1 data-testid="lab-title" className="mt-3 font-heading text-3xl font-black tracking-tight sm:text-4xl">{lab.title}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{lab.lesson}</p>
      {data.briefing && (
        <details data-testid="lab-read-first" open={!data.attempts?.length} className="group mt-6 rounded-lg border border-border bg-card p-5">
          <summary data-testid="lab-read-first-toggle" className="flex cursor-pointer items-center gap-2 font-heading text-lg font-bold"><BookOpen className="h-5 w-5 text-brand-primary" />Read first: how to do this lab</summary>
          <div className="reader simple-reader mt-4"><ReactMarkdown remarkPlugins={[remarkGfm]}>{data.briefing}</ReactMarkdown></div>
        </details>
      )}
      <div className="mt-6"><Stepper step={step} /></div>
      {result ? (
        <div className="mt-10">
          <LabResult r={result} lab={lab} />
          <div className="mt-10 flex flex-wrap gap-3">
            <button onClick={tryAgain} data-testid="lab-retry-button" className="inline-flex items-center gap-2 rounded-sm border border-border px-4 py-2 text-sm font-semibold hover:bg-muted"><RotateCcw className="h-4 w-4" /> Try alternate path</button>
            <Link to={`/modules/${lab.module_id}`} data-testid="lab-continue-button" className="inline-flex items-center gap-2 rounded-sm bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-fg">Continue <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      ) : (
        <>
          {retry && (
            <div data-testid="lab-retry-banner" className="mt-6 border-l-4 border-brand-data bg-card p-4 text-sm">
              <div className="font-semibold">Alternate path · attempt {attempts.length + 1} (previous best {Math.max(...attempts.map((a) => a.total))}%)</div>
              <p className="mt-1">{lab.retry_situation.situation}</p>
              <p className="mt-1 text-muted-foreground">{lab.retry_situation.change}</p>
            </div>
          )}
          <div className="mt-8 grid gap-6 lg:grid-cols-12">
            <div className="space-y-4 lg:col-span-5">
              <div className="border border-border bg-card p-5 text-sm">
                <div className="overline-label">Case</div>
                <p className="mt-2 leading-relaxed">{lab.situation}</p>
                <p className="mt-3"><strong>Your role:</strong> {lab.learner_role}</p>
                <p className="mt-1"><strong>Goal:</strong> {lab.learning_objective}</p>
              </div>
              <div className="border border-border bg-card p-5 text-sm">
                <div className="overline-label">Constraints</div>
                <ul className="mt-2 list-disc space-y-1 pl-5">{lab.constraints.map((c, i) => <li key={i}>{typeof c === "string" ? c : <AnswerView value={c} />}</li>)}</ul>
              </div>
            </div>
            <div className="lg:col-span-7"><div className="overline-label mb-3">Evidence cards · click to inspect</div><Evidence cards={lab.evidence_cards} /></div>
          </div>
          <section className="mt-12 space-y-8">
            {lab.tasks.map((t, i) => (
              <div key={t.id} data-testid={`lab-task-${t.id}`} className="border-t border-border pt-6">
                <div className="font-mono text-xs text-brand-primary">Task {i + 1} · {t.interaction_type.replace(/_/g, " ")}</div>
                <p className="mt-2 font-semibold leading-snug">{t.prompt}</p>
                <p className="mt-1 text-xs text-muted-foreground">{t.expected_input}</p>
                <div className="mt-4 space-y-4">
                  {t.groups.map((g, gi) => <TaskGroup key={g.key + gi} g={g} testId={`lab-${t.id}-g${gi}`} selected={tasks[t.id]?.selected || []} onChange={(sel) => setTask(t.id, { selected: sel })} />)}
                </div>
                <Textarea data-testid={`lab-${t.id}-explain`} rows={2} className="mt-4 rounded-sm" placeholder="Explain why (40+ characters)…" value={tasks[t.id]?.text || ""} onChange={(e) => setTask(t.id, { text: e.target.value })} />
              </div>
            ))}
            <div className="border-t border-border pt-6">
              <div className="font-mono text-xs text-brand-action">Final decision</div>
              <p className="mt-2 font-semibold">Which overall design or decision do you recommend?</p>
              <div className="mt-4 grid gap-2">
                {lab.choices.map((c) => (
                  <label key={c.id} className={`flex cursor-pointer items-start gap-3 border p-3 text-sm ${choice === c.id ? "border-brand-action bg-brand-action/10" : "border-border bg-card"}`}>
                    <input type="radio" name="choice" checked={choice === c.id} onChange={() => setChoice(c.id)} data-testid={`lab-choice-${c.id}`} className="mt-1 accent-[hsl(var(--brand-action))]" />{c.label}
                  </label>
                ))}
              </div>
              <Textarea data-testid="lab-explanation" rows={4} className="mt-4 rounded-sm" placeholder="Explain and defend your decision (80+ characters)…" value={explanation} onChange={(e) => setExplanation(e.target.value)} />
              <div className="mt-1 text-xs text-muted-foreground">{explanation.length}/80</div>
            </div>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-xl">
                <button type="button" data-testid="lab-hint-button" onClick={() => setHints(1)} className="inline-flex items-center gap-2 text-sm font-semibold text-brand-primary"><Lightbulb className="h-4 w-4" /> {hints ? "Hint" : "Ask for hint"}</button>
                {hints > 0 && <p data-testid="lab-hint-text" className="mt-2 border-l-2 border-brand-primary pl-3 text-sm">{lab.hint}</p>}
              </div>
              <button onClick={submit} disabled={busy || !choice || explanation.length < 80} data-testid="lab-submit-button" className="rounded-sm bg-brand-action px-6 py-3 font-semibold text-brand-action-fg disabled:opacity-45">{busy ? "Scoring…" : "Submit decision"}</button>
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}
